import * as React from "react";
import { Typography, Link, Stack } from "@mui/material";
import { GridRenderCellParams, GridRowsProp, GridColDef } from "@mui/x-data-grid-premium";
import DataTable, { CellSize } from "../table";
import TableCellAsset from "../TableCellAsset/table-cell-asset";
import TableCellProviderStatus from "../TableCellProviderStatus";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import moment from "moment";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { routePaths } from "../../../Router/router";
import { Link as RouterLink } from "react-router";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import AssetStatus from "../../Misc/Status/AssetStatus/asset-status";
import { useTranslation } from "react-i18next";
import { DataGridProProps, GridActionsCellItem, GridGroupingColDefOverride } from "@mui/x-data-grid-pro";
import { UnfoldLessOutlined, UnfoldMoreOutlined } from "@mui/icons-material";
import TableUuids from "../table-uuids";
import { GetEnumValueLabelMap } from "../../../Utils/helpers";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";

export type ProvidersTableProps = {
  rows: {
    title: string;
    type?: string;
    desc?: string;
    id: string;
    assetId: string;
    status: string;
    update: string;
    path?: string[];
  }[];
  isHistory?: boolean;
  assets?: DbAssetObjectInfos[];
  additionalFilters?: React.JSX.Element;
};

export interface ExportHandle {
  export: () => void;
}

const ProvidersTable = React.forwardRef<ExportHandle, ProvidersTableProps>((props, ref) => {
  const { t } = useTranslation();
  const rows: GridRowsProp = props.rows;
  type Row = (typeof rows)[number];
  const apiGrid = useGridApiRef();

  React.useImperativeHandle(ref, () => ({
    export: handleExport,
  }));

  const getTreeDataPath: DataGridProProps["getTreeDataPath"] = (row) => {
    return row.path;
  };

  const ExpandableButton = (id: any) => {
    const [expanded, setExpanded] = React.useState(false);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
      apiGrid.current.setRowChildrenExpansion(id.id, !expanded);
    };

    return (
      <GridActionsCellItem
        icon={
          expanded ? (
            <UnfoldLessOutlined sx={{ width: 20, height: 20, color: "text.primary" }} />
          ) : (
            <UnfoldMoreOutlined sx={{ width: 20, height: 20, color: "text.primary" }} />
          )
        }
        label={expanded ? "Collapse" : "Expand"}
        title={expanded ? "Collapse" : "Expand"}
        onClick={handleExpandClick}
      />
    );
  };

  const CUSTOM_GROUPING_COL_DEF: GridGroupingColDefOverride = {
    headerName: "",
    leafField: "id",
    width: 0,
    flex: 0,
    renderCell: (data) => data?.row?.assetId && <ExpandableButton id={data?.row?.id} />,
  };

  const enrichedRows: Row[] = React.useMemo(() => {
    if (!props.assets) return props.rows;
    const finalRows: Row[] = [];

    props.rows.map((row) => {
      finalRows.push({ path: [row.assetId], onlyPicto: true, ...row });
      if (props.assets !== undefined) {
        const asset = props.assets.find((asset) => asset.id === parseInt(row.id));
        if (asset && asset.statusHistory && asset.statusHistory.length > 0) {
          let statuses: Row[] = [];
          asset.statusHistory.map((history, index) => {
            statuses.push({
              id: asset.id.toString() + index.toString(),
              title: "",
              update: history.date ? moment(history.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL).toString() : "-",
              status: history.status,
              path: [asset.id.toString(), asset.id.toString() + index.toString()],
            });
          });
          finalRows.push(...statuses);
        }
      }
    });

    return finalRows;
  }, [props.rows]);

  const handleExport = () => {
    apiGrid.current.exportDataAsCsv({ delimiter: GlobalConst.EXPORT_DELIMITER });
  };

  const columns: GridColDef[] = [
    {
      field: "assetId",
      headerName: t("common:assetId"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      cellClassName: "MuiDataGrid-cell-id",
      renderCell: (data: GridRenderCellParams<Row["assetId"]>) =>
        data?.row?.assetId ? (
          <Stack direction="row" gap={1}>
            <Link
              underline="hover"
              color="primary.text"
              sx={{ opacity: props.isHistory ? 0 : 1 }}
              component={RouterLink}
              to={`${routePaths.asset.path}/${data.value}`}
            >
              {data.value}
            </Link>
          </Stack>
        ) : (
          <></>
        ),
    },
    {
      field: "title",
      headerName: t("common:assetNameUpperCase"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: GridRenderCellParams<Row["title"]>) => {
        return data?.row?.assetId ? (
          <TableCellAsset data={data} />
        ) : (
          <TableCellProviderStatus data={{ value: data.value as string }} />
        );
      },
    },
    {
      field: "update",
      headerName: t("common:update"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: GridRenderCellParams<Row["update"]>) => (
        <Typography color="text.secondary">{data.value}</Typography>
      ),
    },
    {
      field: "status",
      headerName: t("common:status"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectStatus),
      renderCell: (data: GridRenderCellParams<Row["status"]>) => {
        return data?.row?.assetId ? (
          <AssetStatus status={data.value} />
        ) : (
          <Typography color="text.secondary">{data?.row?.status}</Typography>
        );
      },
    },
  ];

  return (
    <DataTable
      tableKey={TableUuids.PROVIDER_INGEST_TABLE}
      rows={enrichedRows}
      columns={columns}
      rowHeight={56}
      getRowHeight={() => "auto"}
      apiRef={apiGrid}
      disableRowSelectionOnClick
      treeData
      getTreeDataPath={getTreeDataPath}
      groupingColDef={CUSTOM_GROUPING_COL_DEF}
      additionalFilters={props.additionalFilters}
    />
  );
});

export default ProvidersTable;
